import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { AdminLayout } from '../../components/AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Upload, X, UserCheck } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const defaultForm = { name: '', designation: '', bio: '', desk_message: '', photo_url: '', order: 0 };

const AdminDirectors = () => {
  const { token } = useAuth();
  const [directors, setDirectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDirector, setEditingDirector] = useState(null);
  const [formData, setFormData] = useState(defaultForm);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => { fetchDirectors(); }, []);

  const fetchDirectors = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/directors`);
      setDirectors(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      let submitData = { ...formData };

      if (photoFile) {
        const uploadForm = new FormData();
        uploadForm.append('file', photoFile);
        const uploadRes = await axios.post(
          `${API_URL}/api/uploads?file_type=general`,
          uploadForm,
          { headers }
        );
        submitData = { ...submitData, photo_url: uploadRes.data.file_url.replace(/^https?:\/\/[^/]+/, '') };
      }

      if (editingDirector) {
        await axios.put(`${API_URL}/api/directors/${editingDirector.id}`, submitData, { headers });
        toast.success('Director updated!');
      } else {
        await axios.post(`${API_URL}/api/directors`, submitData, { headers });
        toast.success('Director added!');
      }
      setDialogOpen(false);
      resetForm();
      fetchDirectors();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this director?')) return;
    try {
      await axios.delete(`${API_URL}/api/directors/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Director deleted!');
      fetchDirectors();
    } catch { toast.error('Failed to delete'); }
  };

  const openEdit = (d) => {
    setEditingDirector(d);
    setFormData({ name: d.name, designation: d.designation, bio: d.bio, desk_message: d.desk_message, photo_url: d.photo_url || '', order: d.order || 0 });
    setPhotoFile(null);
    setPhotoPreview(null);
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingDirector(null);
    setFormData(defaultForm);
    setPhotoFile(null);
    setPhotoPreview(null);
  };

  return (
    <AdminLayout title="Directors Management">
      <div className="flex justify-between items-center mb-6">
        <p className="text-muted-foreground">Manage institute directors displayed on the About page.</p>
        <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
          <DialogTrigger asChild>
            <Button><Plus className="w-4 h-4 mr-2" /> Add Director</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingDirector ? 'Edit' : 'Add'} Director</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Photo Upload */}
              <div className="space-y-2">
                <Label>Photo</Label>
                <div
                  className="border-2 border-dashed rounded-lg cursor-pointer hover:border-primary transition-colors"
                  onClick={() => document.getElementById('director-photo-input').click()}
                >
                  {photoPreview || formData.photo_url ? (
                    <div className="relative">
                      <img
                        src={photoPreview || (formData.photo_url?.startsWith('http') ? formData.photo_url : `${API_URL}${formData.photo_url}`)}
                        alt="Preview"
                        className="w-full h-48 object-cover rounded-lg"
                      />
                      <button
                        type="button"
                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1"
                        onClick={(e) => { e.stopPropagation(); setPhotoFile(null); setPhotoPreview(null); setFormData({ ...formData, photo_url: '' }); }}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2 py-8 text-muted-foreground">
                      <Upload className="w-8 h-8" />
                      <span className="text-sm">Click to upload photo</span>
                    </div>
                  )}
                </div>
                <input
                  id="director-photo-input"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handlePhotoChange}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Name *</Label>
                  <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
                </div>
                <div className="space-y-2">
                  <Label>Order</Label>
                  <Input type="number" value={formData.order} onChange={(e) => setFormData({ ...formData, order: Number(e.target.value) })} />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Designation *</Label>
                <Input value={formData.designation} onChange={(e) => setFormData({ ...formData, designation: e.target.value })} placeholder="e.g. Co-Founder & Director" required />
              </div>

              <div className="space-y-2">
                <Label>Short Bio *</Label>
                <Textarea
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  placeholder="Brief description shown on the card..."
                  rows={3}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>From the Director's Desk *</Label>
                <Textarea
                  value={formData.desk_message}
                  onChange={(e) => setFormData({ ...formData, desk_message: e.target.value })}
                  placeholder="Detailed message shown in the popup..."
                  rows={5}
                  required
                />
              </div>

              <div className="flex gap-2 justify-end pt-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Saving...' : editingDirector ? 'Update' : 'Add Director'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Loading...</div>
      ) : directors.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <UserCheck className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
            <p className="text-muted-foreground">No directors added yet.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          {directors.map((d) => (
            <Card key={d.id}>
              <CardContent className="pt-6">
                <div className="flex gap-4">
                  {d.photo_url ? (
                    <img
                      src={d.photo_url?.startsWith('http') ? d.photo_url : `${API_URL}${d.photo_url}`}
                      alt={d.name}
                      className="w-20 h-20 rounded-full object-cover flex-shrink-0"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <UserCheck className="w-8 h-8 text-primary" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h3 className="font-semibold text-lg leading-tight">{d.name}</h3>
                        <p className="text-sm text-primary font-medium">{d.designation}</p>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <Button variant="outline" size="icon" onClick={() => openEdit(d)}><Edit className="w-4 h-4" /></Button>
                        <Button variant="outline" size="icon" onClick={() => handleDelete(d.id)}><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{d.bio}</p>
                    <p className="text-xs text-muted-foreground mt-1">Order: {d.order}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminDirectors;
